import { useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import type { StaffNotif } from '../hooks/useNotifications';
import { useTableRequests } from '../hooks/useTableRequests';
import type { TableRequest } from '../hooks/useTableRequests';
import BottomNav from '../components/BottomNav';

const NOTIF_META: Record<StaffNotif['type'], { icon: string; dotColor: string }> = {
  payment_received: { icon: 'contactless',     dotColor: '#10B981' },
  help_requested:   { icon: 'campaign',        dotColor: '#000000' },
  cash_pending:     { icon: 'payments',        dotColor: '#6B7280' },
  table_freed:      { icon: 'check_circle',    dotColor: '#6B7280' },
};

function notifText(n: StaffNotif): string {
  const num = n.table_number != null ? `Table ${n.table_number}` : '—';
  const amt = n.amount != null
    ? n.amount.toLocaleString('fr-MA', { minimumFractionDigits: 2 }) + ' MAD'
    : '';
  switch (n.type) {
    case 'payment_received': return `${num} · ${amt}`;
    case 'help_requested':   return `${num} demande de l'aide`;
    case 'cash_pending':     return `${num} attend encaissement cash`;
    case 'table_freed':      return `${num} libérée`;
  }
}

function relativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h`;
}

const REQ_TYPE_LABEL: Record<string, string> = {
  call_waiter: 'Demande serveur',
  help: 'Demande serveur',
  request_bill: "Demande l'addition",
  request_cash: 'Demande encaissement cash',
};

function RequestRow({
  req,
  busy,
  onAck,
  onResolve,
}: {
  req: TableRequest;
  busy: boolean;
  onAck: () => void;
  onResolve: () => void;
}) {
  const acked = req.status === 'ack' || Boolean(req.ack_at);
  return (
    <div
      className="px-4 py-3 bg-surface-container-low flex flex-col gap-2"
      style={{ borderBottom: '0.5px solid #e5e7eb' }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-on-surface-strong">
            {req.table_number != null ? `Table ${req.table_number}` : '—'} ·{' '}
            {REQ_TYPE_LABEL[req.type] ?? req.type}
          </p>
          <p className="text-[10px] text-on-surface-variant">
            {relativeTime(req.created_at)}
            {acked ? ' · reçue par le serveur' : ''}
          </p>
        </div>
        <span className="material-symbols-outlined text-[18px] text-on-surface-variant">
          campaign
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={busy || acked}
          onClick={onAck}
          className="min-h-[40px] text-[11px] font-semibold uppercase tracking-wider text-on-surface bg-surface disabled:opacity-50"
          style={{ border: '0.5px solid #000' }}
        >
          Accepter
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onResolve}
          className="min-h-[40px] text-[11px] font-semibold uppercase tracking-wider text-on-primary bg-primary disabled:opacity-50"
        >
          Résolue
        </button>
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const { session } = useAuth();
  const { notifs, unreadCount, markRead, markAllRead } = useNotifications(session?.storeId ?? '');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { requests, ack, resolve } = useTableRequests(session?.storeId ?? '', {
    onInsert: () => {
      try {
        if (!audioRef.current) audioRef.current = new Audio('/sounds/new-order.mp3');
        const a = audioRef.current;
        a.currentTime = 0;
        void a.play().catch(() => undefined);
      } catch {
        // best effort
      }
    },
  });

  useEffect(() => {
    return () => markAllRead();
  }, [markAllRead]);

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <p className="text-sm font-bold text-on-surface-strong">Notifications</p>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-xs text-on-surface-variant min-h-[36px] px-2"
          >
            Tout marquer lu
          </button>
        )}
      </header>

      {/* Demandes serveur */}
      <section>
        <div className="px-4 py-2 flex items-center justify-between bg-background">
          <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
            Demandes serveur
          </span>
          <span className="text-[10px] text-on-surface-variant tabular-nums">
            {requests.length}
          </span>
        </div>
        {requests.length === 0 ? (
          <div
            className="px-4 py-3 bg-surface text-sm text-on-surface-variant"
            style={{ borderTop: '0.5px solid #e5e7eb', borderBottom: '0.5px solid #e5e7eb' }}
          >
            Aucune demande en attente
          </div>
        ) : (
          <div style={{ borderTop: '0.5px solid #e5e7eb' }}>
            {requests.map((r) => (
              <RequestRow
                key={r.id}
                req={r}
                busy={false}
                onAck={() => void ack(r.id)}
                onResolve={() => void resolve(r.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Notifications classiques */}
      {notifs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <span className="material-symbols-outlined text-[48px] text-on-surface-variant">notifications_none</span>
          <p className="text-sm text-on-surface-variant">Aucune notification</p>
        </div>
      ) : (
        <div>
          {notifs.map((n) => {
            const meta = NOTIF_META[n.type];
            return (
              <button
                key={n.id}
                onClick={() => markRead(n.id)}
                className={`w-full flex items-center gap-4 px-4 py-3 min-h-[64px] text-left transition-none ${
                  n.read ? 'bg-surface' : 'bg-surface-container-low'
                }`}
                style={{ borderBottom: '0.5px solid #e5e7eb' }}
              >
                <div
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: n.read ? 'transparent' : meta.dotColor }}
                />
                <span className={`material-symbols-outlined text-[20px] shrink-0 ${n.read ? 'text-on-surface-variant' : 'text-on-surface'}`}>
                  {meta.icon}
                </span>
                <span className="flex-1 text-sm text-on-surface">{notifText(n)}</span>
                <span className="text-[10px] text-on-surface-variant shrink-0">{relativeTime(n.created_at)}</span>
              </button>
            );
          })}
        </div>
      )}

      <BottomNav unreadCount={unreadCount} />
    </div>
  );
}
